'use client';

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { pageVariants, fadeInVariants } from '@/lib/animations';
import SidebarNav from './SidebarNav';
import CustomerPanelHeader from './CustomerPanelHeader';
import CustomerDashboard from '@/components/page_layouts/CustomerDashboard';
import CustomerProfile from '@/components/page_layouts/CustomerProfile';
import CustomerReservations from '@/components/page_layouts/CustomerReservations';
import CustomerFavorites from '@/components/page_layouts/CustomerFavorites';
import CustomerNewsletter from '@/components/page_layouts/CustomerNewsletter';
import CustomerSettings from '@/components/page_layouts/CustomerSettings';
import CustomerInvoices from '@/components/page_layouts/CustomerInvoices';
import type { Country } from '@/contexts/CountriesContext';
import { useCountries } from '@/contexts/CountriesContext';
import type { PageUrlMap } from '@/types/blog';
import { UrlBuilder } from '@/lib/url-builder';
import { CustomerDashboardProvider } from '@/contexts/CustomerDashboardContext';

// Customer type tanımı (CustomerAuthContext'teki ile uyumlu)
type Customer = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  email_verified_at?: string;
  phone_verified_at?: string | null;
  newsletter_subscribed: boolean;
  account_status: string;
  [key: string]: unknown;
};

interface CustomerPanelLayoutProps {
  children?: React.ReactNode;
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  /** @deprecated Ülke listesi artık CountriesProvider üzerinden gelir; prop kullanılmıyor */
  countries?: Country[];
}

// Component mapping - page key'lerine göre component'leri map et
const componentMap: Record<string, React.ComponentType<Record<string, unknown>>> = {
  dashboard: CustomerDashboard,
  profile: CustomerProfile,
  reservations: CustomerReservations,
  invoices: CustomerInvoices,
  favorites: CustomerFavorites,
  newsletter: CustomerNewsletter,
  settings: CustomerSettings,
};

function resolveLoginUrl(pageUrls: PageUrlMap | undefined, language: string, defaultLanguage: string): string {
  if (pageUrls?.['login']) {
    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
    const loginUrl = builder.buildUrl('login', {});
    if (loginUrl && loginUrl !== '#') {
      return loginUrl;
    }
    const loginUrlObj = pageUrls['login'];
    if (typeof loginUrlObj === 'object' && loginUrlObj[language]) {
      return loginUrlObj[language];
    }
    if (typeof loginUrlObj === 'string') {
      return loginUrlObj;
    }
  }
  return `/${language}/login`;
}

function CustomerPanelLayout({
  children,
  language = 'en',
  translations = {},
  pageUrls,
  defaultLanguage = 'en',
  countries: _countriesProp = [],
}: CustomerPanelLayoutProps) {
  const countries = useCountries();
  const { customer, isLoading, refreshCustomer } = useCustomerAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hasRedirectedRef = useRef(false);
  const authRetryRef = useRef(false);
  const t = useCustomerPanelTranslations(translations);

  useEffect(() => {
    if (isLoading) return;

    if (customer) {
      hasRedirectedRef.current = false;
      authRetryRef.current = false;
      return;
    }

    if (!authRetryRef.current) {
      authRetryRef.current = true;
      void refreshCustomer();
      return;
    }

    if (hasRedirectedRef.current) return;

    const timer = window.setTimeout(() => {
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;
      const loginUrl = resolveLoginUrl(pageUrls, language, defaultLanguage);
      const returnUrl = pathname ? encodeURIComponent(pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')) : '';
      const finalRedirectUrl = returnUrl ? `${loginUrl}?return=${returnUrl}` : loginUrl;
      router.replace(finalRedirectUrl);
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [customer, isLoading, router, pathname, searchParams, pageUrls, language, defaultLanguage, refreshCustomer]);

  // Query parameter'dan page'i oku (varsayılan: dashboard)
  // URL'den direkt okuma da yap (fallback)
  const currentPage = useMemo(() => {
    let pageParam = searchParams?.get('page');
    
    // Eğer searchParams null ise, window.location'dan oku (client-side fallback)
    if (!pageParam && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      pageParam = urlParams.get('page');
    }
    
    return pageParam && componentMap[pageParam] ? pageParam : 'dashboard';
  }, [searchParams, children]);

  // Render edilecek component'i belirle
  const PageComponent = useMemo(() => {
    return componentMap[currentPage] || CustomerDashboard;
  }, [currentPage]);

  // Component props'ları
  const componentProps = useMemo(() => ({
    language,
    translations,
    pageUrls,
    defaultLanguage,
    countries,
  }), [language, translations, pageUrls, defaultLanguage, countries]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  if (isLoading || !customer) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-gradient-to-b from-gray-100/80 to-white" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-alsero-first/30 border-t-alsero-first" aria-hidden="true"></div>
        <span className="sr-only">{t('customer_panel.common.loading', 'Loading...')}</span>
      </div>
    );
  }

  return (
    <CustomerDashboardProvider>
    <motion.div 
      className="bg-gradient-to-b from-gray-100/90 via-gray-50/50 to-white min-h-[60vh]"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden backdrop-blur-sm"
            onClick={closeSidebar}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeSidebar();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('customer_panel.common.close_menu', 'Close menu')}
          />
        )}
      </AnimatePresence>

      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:mt-[120px]">
        {/* Mobile sidebar toggle button */}
        <div className="lg:hidden fixed top-16 right-4 z-30">
          <button type="button"
            onClick={toggleSidebar}
            className="p-2 rounded-md bg-white drop-shadow-card border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2"
            aria-label={t('customer_panel.common.menu', 'Menu')}
            aria-expanded={sidebarOpen}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page content - Grid layout */}
        <main className="" id="main-content">
          <div className="grid grid-cols-12 gap-8">
            {/* Sidebar - Left side (3 columns on large screens) */}
            <div className="col-span-12 lg:col-span-3 self-start">
              <div className="rounded-2xl border border-gray-200/80 bg-white/90 shadow-tour-card backdrop-blur-sm px-2 py-4 lg:sticky lg:top-28">
                <SidebarNav
                  isOpen={sidebarOpen}
                  onClose={closeSidebar}
                  language={language}
                  translations={translations}
                  pageUrls={pageUrls}
                  defaultLanguage={defaultLanguage}
                />
              </div>
            </div>

            {/* Main content - Right side (9 columns on large screens) */}
            <motion.div 
              key={currentPage}
              className="col-span-12 lg:col-span-9"
              variants={fadeInVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Header - Customer bilgisi ve yaklaşan rezervasyonlar */}
              <CustomerPanelHeader
                language={language}
                translations={translations}
              />
              
              <div className="rounded-2xl border border-gray-200/80 bg-white/95 shadow-tour-card backdrop-blur-sm px-4 sm:px-6 py-5 sm:py-6">
                {/* Query parameter varsa onu kullan (öncelik), yoksa children kullan (geriye dönük uyumluluk) */}
                {(() => {
                  // Önce searchParams'tan oku, yoksa window.location'dan oku
                  let pageParam = searchParams?.get('page');
                  if (!pageParam && typeof window !== 'undefined') {
                    const urlParams = new URLSearchParams(window.location.search);
                    pageParam = urlParams.get('page');
                  }
                  
                  const hasValidPageParam = pageParam && componentMap[pageParam];
                  
                  if (hasValidPageParam) {
                    return <PageComponent {...componentProps} />;
                  } else if (children) {
                    return children;
                  } else {
                    return <PageComponent {...componentProps} />;
                  }
                })()}
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </motion.div>
    </CustomerDashboardProvider>
  );
}

// Layout'u memoize etme - children değiştiğinde re-render edilmeli
// Sidebar zaten memoize edilmiş, sadece sidebar props'ları değiştiğinde re-render edilir
export default CustomerPanelLayout;

